import type React from 'react';
import {
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesCheckbox,
  AgnesInput,
  AgnesInputFile,
  AgnesLabel,
  AgnesPageHeader,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesTextarea,
} from '@agnes/ui';
import { HelpCircle, Mail, MessageCircle } from 'lucide-react';
import { NeoAssistCentral, neoAssistEnabled } from '@/components/NeoAssistCentral';

const ASSUNTOS = [
  { value: 'esocial', label: 'eSocial' },
  { value: 'certidoes', label: 'Certidões' },
  { value: 'encargos', label: 'Encargos' },
  { value: 'acesso', label: 'Acesso / Login' },
  { value: 'outro', label: 'Outro' },
];

export default function PageCentralAjuda() {
  // NeoAssist ligado → embed real (widget de terceiro). Senão → form fallback (protótipo).
  if (neoAssistEnabled()) {
    return (
      <>
        <AgnesPageHeader
          icon={<HelpCircle />}
          title="Central de Relacionamento"
          subtitle="Fale conosco"
          breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Central de Relacionamento' }]}
        />
        <NeoAssistCentral />
      </>
    );
  }

  return (
    <>
      <AgnesPageHeader
        icon={<HelpCircle />}
        title="Central de Relacionamento"
        subtitle="Preencha o formulário e retornaremos o seu contato."
        breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Ajuda' }]}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <AgnesCard className="lg:col-span-2">
          <AgnesCardContent className="flex items-stretch">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                alert('Mensagem enviada (protótipo)');
              }}
              className="flex flex-col gap-4"
            >
              <div>
                <AgnesLabel required>Nome</AgnesLabel>
                <AgnesInput placeholder="Digite seu nome" />
              </div>
              <div>
                <AgnesLabel required>E-mail</AgnesLabel>
                <AgnesInput type="email" placeholder="Digite seu e-mail" />
              </div>
              <div>
                <AgnesLabel required>Telefone</AgnesLabel>
                <AgnesInput placeholder="Digite seu telefone" />
              </div>
              <div>
                <AgnesLabel required>Assunto</AgnesLabel>
                <AgnesSelect>
                  <AgnesSelectTrigger>
                    <AgnesSelectValue placeholder="Selecione um assunto" />
                  </AgnesSelectTrigger>
                  <AgnesSelectContent>
                    {ASSUNTOS.map((a) => (
                      <AgnesSelectItem key={a.value} value={a.value}>
                        {a.label}
                      </AgnesSelectItem>
                    ))}
                  </AgnesSelectContent>
                </AgnesSelect>
              </div>
              <div>
                <AgnesLabel required>Mensagem</AgnesLabel>
                <AgnesTextarea placeholder="Digite sua mensagem" rows={5} />
              </div>
              <div>
                <AgnesLabel>Anexos</AgnesLabel>
                <AgnesInputFile />
              </div>
              <label className="flex items-center gap-2 text-sm text-agnes-text-muted">
                <AgnesCheckbox /> Eu aceito a política de privacidade *
              </label>
              <div className="flex items-center justify-between">
                <span className="text-xs italic text-agnes-text-muted">
                  * Campos de preenchimento obrigatório
                </span>
                <AgnesButton type="submit" variant="primary">
                  Enviar
                </AgnesButton>
              </div>
            </form>
          </AgnesCardContent>
        </AgnesCard>

        <AgnesCard className="flex flex-col gap-6">
          <AgnesCardContent className="flex items-stretch">
            <h3 className="font-semibold text-agnes-text-primary">
              Ainda com dúvidas? Entre em contato:
            </h3>

            <ContatoItem
              icon={<Mail className="h-5 w-5" />}
              titulo="E-mail"
              acao="Abrir formulário"
            />
            <ContatoItem
              icon={<MessageCircle className="h-5 w-5" />}
              titulo="Conversa"
              acao="Iniciar conversa"
            />
            <ContatoItem icon={<WhatsIcon />} titulo="WhatsApp" acao="Iniciar conversa" />
          </AgnesCardContent>
        </AgnesCard>
      </div>
    </>
  );
}

function ContatoItem({
  icon,
  titulo,
  acao,
}: Readonly<{ icon: React.ReactNode; titulo: string; acao: string }>) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-agnes-primary-hover text-agnes-primary">
        {icon}
      </div>
      <div>
        <div className="font-medium text-agnes-text-primary">{titulo}</div>
        <button className="text-sm text-agnes-primary underline">{acao}</button>
      </div>
    </div>
  );
}

function WhatsIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
      <path d="M17.5 14.4c-.3-.2-1.7-.8-2-.9-.3-.1-.5-.2-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-1.7-.9-2.8-1.6-3.9-3.5-.3-.5.3-.5.8-1.5.1-.2 0-.4 0-.5 0-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.3 5.2 4.6 2 .8 2.7.9 3.7.8.6-.1 1.7-.7 2-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.4M12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2" />
    </svg>
  );
}
